import * as React from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { IApiAdQueryParams, IApiAdTargeting } from 'redux-state/advertisement/types.js';
import { useAppSelector } from 'redux-state/configure-store.js';
import { IRepoState } from 'redux-state/rootReducer.js';
import { IMarker } from 'types/markerCore/marker-core-type.js';
import { addBBQQueryParamsTargeting } from './targeting-dynamic-bbq.js';

/**
 * This is used if we want to add any spcific ad targetting based on url query parameters
 * Currently only used for bbq-forecast
 */
export const addQueryParamsTargeting = ({
  propNameTargetValue,
  urlQueryParams,
  markerData,
}: {
  propNameTargetValue: IApiAdQueryParams;
  urlQueryParams: URLSearchParams;
  markerData: IMarker[] | undefined;
}): IApiAdTargeting | undefined => {
  const { param, type } = propNameTargetValue;

  if (type === 'bbq-forecast-page') {
    const result = addBBQQueryParamsTargeting({ propNameTargetValue, urlQueryParams, markerData });
    return result;
  }

  /** e.g. ['range', 'currentRange']  => { 'currentRange': '200' }*/
  const value = urlQueryParams.get(param);
  if (value) {
    return { key: type, value };
  }
};

/** Builds targetting if there are adQueryParams */
export const BuildDynamicQueryTargetingCore = ({
  adQueryParams,
  urlQueryParams,
}: {
  urlQueryParams: URLSearchParams;
  adQueryParams: IApiAdQueryParams[] | undefined;
}) => {
  const rawMarkers = useSelector((state: IRepoState) => state.LAYOUT.screen.layout.map?.markers);
  const [newTargetingData, setNewTargetingData] = React.useState<IApiAdTargeting[]>([]);
  const markerData = rawMarkers;
  React.useEffect(() => {
    if (adQueryParams && urlQueryParams) {
      const targetingProps = adQueryParams.reduce<IApiAdTargeting[]>((acc, propNameTargetValue) => {
        const newTargeting = addQueryParamsTargeting({
          urlQueryParams,
          markerData,
          propNameTargetValue,
        });
        if (newTargeting) {
          const newAcc = acc.concat(newTargeting);
          return newAcc;
        }
        return acc;
      }, [] as IApiAdTargeting[]);

      if (targetingProps.length > 0) {
        setNewTargetingData(targetingProps);
      }
    }
  }, [urlQueryParams, adQueryParams, markerData]);

  return newTargetingData;
};

/** Get additional targeting data from query params */
export const BuildDynamicQueryTargeting = ({
  overrideBaseTargeting,
  queryParams,
}: {
  overrideBaseTargeting: boolean;
  queryParams?: IApiAdQueryParams[];
}) => {
  const baseQueryParams = useAppSelector((state) => state.ADVERTISEMENT.queryParams);
  const [urlQueryParams] = useSearchParams();
  const adQueryParams = overrideBaseTargeting ? queryParams : baseQueryParams;
  const adQueryTargeting = BuildDynamicQueryTargetingCore({ adQueryParams, urlQueryParams });
  return adQueryTargeting;
};
